import FlowObject, {
  actionFromJSON,
  parseFrame,
  FrameVal,
  MixedMediaObjectType,
} from "./FlowObject";
import guidGenerator, {
  getCurrentDate,
} from "../features/utils/commonFunctions";
import Color from "./Color";
import ImageAsset, { PdfAsset } from "./Assets";
import GalleryAsset from "./GalleryAsset";
import Action from "./Action";
import { UploadResponse } from "./Screens";
import {
  ChildEvents,
  ChildMachineSchema,
  EventKind,
  FlowObjects,
  IndexName,
  IndexTypes,
  Kind,
} from "../features/fsm/types";
import { Interpreter } from "xstate";
import * as Sentry from "@sentry/react"

export default class WebFlowObject implements Kind {
  id: string;
  borderColor: Color;
  rotation: 0 | 1 | 2 | 3;
  galleryAssets: GalleryAsset | null;
  updated: string;
  frame: FrameVal;
  videoAsset: ImageAsset | null;
  zoomScale: number;
  retinaImageAsset: ImageAsset | null;
  pdfAsset: PdfAsset | null;
  type: MixedMediaObjectType;
  action: Action | null;
  originalImageAsset: ImageAsset | null;
  borderWidth: number;
  zoomOffset: {
    height: number;
    width: number;
  };
  audioAsset: ImageAsset | null;
  croppedImageAsset: ImageAsset | null;
  created: string;
  locked: boolean;
  cornerRadius: number;
  /**
   * Hidden from JSON.parse, this informs video/pdf objects to only use previewImage
   */
  __forceUseImage: boolean;
  /**
   * Associated {@link MediaMachine} for this object
   */
  __ref?: Interpreter<
    { mmo: FlowObjects },
    ChildMachineSchema,
    ChildEvents,
    any
  >;
  webUrl: string | null;
  constructor(
    id: string,
    borderColor: Color,
    rotation: 0 | 1 | 2 | 3,
    galleryAssets: GalleryAsset | null,
    updated: string,
    frame: FrameVal,
    videoAsset: ImageAsset | null,
    zoomScale: number,
    retinaImageAsset: ImageAsset | null,
    pdfAsset: PdfAsset | null,
    type: MixedMediaObjectType,
    action: Action | null,
    originalImageAsset: ImageAsset | null,
    borderWidth: number,
    zoomOffset: {
      height: number;
      width: number;
    },
    audioAsset: ImageAsset | null,
    croppedImageAsset: ImageAsset | null,
    created: string,
    locked: boolean,
    webUrl: string | null,
    cornerRadius: number,
  ) {
    this.id = id;
    this.borderColor = borderColor;
    this.rotation = rotation;
    this.galleryAssets = galleryAssets;
    this.updated = updated;
    this.frame = frame;
    this.videoAsset = videoAsset;
    this.zoomScale = zoomScale;
    this.retinaImageAsset = retinaImageAsset;
    this.pdfAsset = pdfAsset;
    this.type = type;
    this.action = action;
    this.originalImageAsset = originalImageAsset;
    this.borderWidth = borderWidth;
    this.zoomOffset = zoomOffset;
    this.audioAsset = audioAsset;
    this.croppedImageAsset = croppedImageAsset;
    this.created = created;
    this.locked = locked;
    this.__forceUseImage = false;
    this.webUrl = webUrl;
    this.cornerRadius = cornerRadius;
  }

  eventKind(): EventKind {
    return EventKind.Screen;
  }

  static fromJSON(json: any): WebFlowObject {
    let flowObj = Object.create(WebFlowObject.prototype);
    return Object.assign(flowObj, json, {
      action: actionFromJSON(json.action),
      frame: parseFrame(json.frame),
      originalImageAsset: ImageAsset.fromJSON(json.originalImageAsset),
      croppedImageAsset: ImageAsset.fromJSON(json.croppedImageAsset),
      retinaImageAsset: ImageAsset.fromJSON(json.retinaImageAsset),
      borderColor: Color.fromJSON(json.borderColor),
      cornerRadius: json.cornerRadius || 0,
    });
  }

  static reviver(key: string, value: any): any {
    return key === "" ? WebFlowObject.fromJSON(value) : value;
  }

  static default(url: string, width: number, height: number): WebFlowObject {
    return new WebFlowObject(
      guidGenerator(),
      Color.iosToCssColor("00000"),
      0,
      null,
      getCurrentDate(),
      {
        width,
        height,
        x: 200.0,
        y: 200.0,
      },
      null,
      1,
      null,
      null,
      "webview",
      null,
      null,
      0,
      {
        height: 0,
        width: 0,
      },
      null,
      null,
      getCurrentDate(),
      false,
      url,
      0,
    );
  }

  /**
   * Updates object to use CDN image instead of local
   *
   * Needs `indexMap` since backend provides no context to where responses should go.
   *
   * @param response [UploadResponse] a single element array with an `ssl_address` and `treemoContentID`
   * @param indexMap hashmap defining which index in upload response maps to fields on this object
   */
  setMMOUrl(response: UploadResponse[], indexMap: Map<IndexName, number>) {
    let index = indexMap.get(IndexTypes.originalImageAsset);
     Sentry.addBreadcrumb({
				category: 'debug',
				message: 'media upload response',
				data: {response, indexMap},
				level: Sentry.Severity.Info,
				type: 'code',
			});
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.originalImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.originalImageAsset!.treemoContentID = treemoContentID;
      this.originalImageAsset!.updated = getCurrentDate();
    }
    index = indexMap.get(IndexTypes.croppedImageAsset);
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.croppedImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.croppedImageAsset!.treemoContentID = treemoContentID;
      this.croppedImageAsset!.updated = getCurrentDate();
    }
  }
}

WebFlowObject.displayName = "WebFlowObject";
